import { clx } from '@medusajs/ui';

import type { EnhancedStorePromotion } from '@marxbiotech/shared-types/medusa';
import { convertToLocale } from '@lib/util/money';
import Text from '@modules/common/components/text';

import {
  DiscountLabel,
  DiscountLabelKind,
} from '../components/discount-label';

interface ShippingInfoProps {
  total: number;
  showShippingHint: boolean;
  currencyCode: string;
  freeshippingPromotion: EnhancedStorePromotion;
}

const ShippingInfo = ({
  total,
  showShippingHint,
  currencyCode,
  freeshippingPromotion,
}: ShippingInfoProps) => {
  const threshold =
    freeshippingPromotion.custom_metadata?.data?.free_shipping_threshold ?? 0;
  const isFreeShipping = total >= threshold;

  if (!isFreeShipping && !showShippingHint) return null;

  return (
    <div
      className="mt-2 flex items-center gap-2 first:mt-0"
      data-testid="cart-free-shipping-hint"
    >
      <DiscountLabel kind={DiscountLabelKind.FreeShipping}>免運</DiscountLabel>
      <div className="text-blue-30">
        {isFreeShipping ? (
          <span>恭喜！您已獲得免運優惠</span>
        ) : (
          <>
            再買
            <span className="mx-1">
              {convertToLocale({
                amount: threshold - total,
                currency_code: currencyCode,
              })}
            </span>
            即可免運
            <span className="text-grey-60 ml-2">
              全館滿{' '}
              {convertToLocale({
                amount: threshold,
                currency_code: currencyCode,
              })}{' '}
              免運費
            </span>
          </>
        )}
      </div>
    </div>
  );
};

interface GiveawayProps {
  campaign: {
    name: string;
  };
}

const Giveaway = ({ campaign }: GiveawayProps) => {
  return (
    <div
      className="mt-2 flex items-center gap-2 first:mt-0"
      data-testid="cart-discount-hint"
    >
      <DiscountLabel kind={DiscountLabelKind.Giveaway}>贈品</DiscountLabel>
      <div className="text-purple-30">{campaign?.name}</div>
    </div>
  );
};

interface DiscountTemplateProps {
  promotions?: EnhancedStorePromotion[];
  itemTotal: number;
  currencyCode: string;
  hideTitle?: boolean;
  showShippingHint?: boolean;
  className?: string;
  freeshippingPromotion: EnhancedStorePromotion | null;
}

const DiscountTemplate = ({
  promotions,
  itemTotal,
  currencyCode,
  hideTitle = false,
  showShippingHint = false,
  className,
  freeshippingPromotion,
}: DiscountTemplateProps) => {
  if ((!promotions || promotions?.length === 0) && !showShippingHint) {
    return null;
  }

  return (
    <div className={clx('bg-white-10 p-6', className)}>
      {!hideTitle && <Text variant="title-bold" as="h2" className="mb-4">優惠與運費資訊</Text>}
      <div>
        {promotions
          ?.filter(({ code }) => code && !/^FREESHIPPING/.test(code))
          .map((promotion) =>
            promotion.campaign ? (
              <Giveaway key={promotion.code ?? promotion.id} campaign={promotion.campaign} />
            ) : null
          )}
        {freeshippingPromotion && (
          <ShippingInfo
            total={itemTotal}
            showShippingHint={showShippingHint}
            currencyCode={currencyCode}
            freeshippingPromotion={freeshippingPromotion}
          />
        )}
      </div>
    </div>
  );
};

export default DiscountTemplate;
